class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      if (currentButtonText.match(new RegExp(previousTerm, 'g')).length > 1) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

getSearchResults(searchTerm) {
  const queryKey = searchTerm.replace(' ', '-').toLowerCase();
  this.setLiveRegionLoadingState();

  if (this.cachedResults[queryKey]) {
    this.renderSearchResults(this.cachedResults[queryKey]);
    return;
  }

  const fields = 'title,product_type,variants.title,variants.sku,vendor';
  const MAX_COLLECTIONS = 3;

  const sectionUrl =
    `${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}` +
    `&section_id=predictive-search` +
    `&resources[options][fields]=${encodeURIComponent(fields)}`;

  const collectionsJsonUrl =
    `/search/suggest.json?q=${encodeURIComponent(searchTerm)}` +
    `&resources[type]=collection` +
    `&resources[collections][limit]=${MAX_COLLECTIONS}`;

  // Helpers
  const toPath = (href) => {
    try { return new URL(href, window.location.origin).pathname; } catch { return ''; }
  };
  const ensureWidth = (url) => {
    if (!url) return '';
    return url.includes('width=') ? url : `${url}${url.includes('?') ? '&' : '?'}width=100`;
  };
  const fetchFirstProductImage = async (path, signal) => {
    try {
      const res = await fetch(`${path}?view=first-product-image`, { signal, headers: { Accept: 'application/json' } });
      if (!res.ok) return null;
      try {
        const data = await res.json();
        if (data?.image) return (data.image.startsWith('//') ? `https:${data.image}` : data.image);
      } catch {
        const txt = await res.text();
        const m = txt.match(/"image"\s*:\s*"([^"]+)"/);
        if (m) return m[1].startsWith('//') ? `https:${m[1]}` : m[1];
      }
    } catch {}
    return null;
  };

  // Fetch section HTML and collections JSON in parallel
  Promise.all([
    fetch(sectionUrl, { signal: this.abortController.signal }),
    fetch(collectionsJsonUrl, { signal: this.abortController.signal }).catch(() => null) // tolerate failure
  ])
    .then(async ([sectionRes, collectionsRes]) => {
      if (!sectionRes?.ok) {
        const error = new Error(sectionRes?.status || 'Section fetch failed');
        this.close();
        throw error;
      }
      const sectionText = await sectionRes.text();

      let collectionsData = null;
      if (collectionsRes && collectionsRes.ok) {
        try {
          collectionsData = await collectionsRes.json();
        } catch (_) {
          collectionsData = null;
        }
      }

      // Parse section HTML
      const doc = new DOMParser().parseFromString(sectionText, 'text/html');
      const sectionRoot = doc.querySelector('#shopify-section-predictive-search');

      if (!sectionRoot) {
        const raw = doc.body ? doc.body.innerHTML : sectionText;
        this.cacheAndRender(queryKey, raw);
        return;
      }

      // If we have collections JSON results, replace the collections <ul> before rendering
      if (collectionsData?.resources?.results?.collections?.length) {
        const list = sectionRoot.querySelector('#predictive-search-results-collections-list');
        if (list) {
          const ulPlaceholder = list.dataset.placeholderImageUrl || '';

          // Build a MAP of per-item placeholders from the pre-rendered list by URL PATH
          const preLis = Array.from(list.querySelectorAll('li.predictive-search__list-item'));
          const perItemMap = new Map();
          preLis.forEach((li) => {
            const a = li.querySelector('a[href]');
            if (!a) return;
            const path = toPath(a.getAttribute('href'));
            const liPlaceholder = li.dataset?.placeholderImageUrl || '';
            if (path && liPlaceholder) perItemMap.set(path, liPlaceholder);
          });

          // CAP to 8 no matter what Shopify returns
          const collections = collectionsData.resources.results.collections.slice(0, MAX_COLLECTIONS);

          const itemsHtml = collections.map((c, i) => {
            const title = (c.title || '').replace(/"/g, '&quot;');
            const imgUrl = c?.image || c?.featured_image?.url || null;

            const href = c?.url || (c?.handle ? `/collections/${c.handle}` : '#');
            const path = toPath(href);

            const perItemPlaceholder = perItemMap.get(path) || ulPlaceholder || '';

            const needsFirst = !imgUrl; // explicit flag

            const imageBlock = imgUrl
              ? `
                <div class="img-holder">
                  <img
                    class="predictive-search__image"
                    src="${ensureWidth(imgUrl)}"
                    alt="${title}"
                    width="50"
                    height="50"
                    loading="lazy"
                  >
                </div>`
              : `
                <div class="img-holder">
                  <img
                    class="predictive-search__image"
                    src="${perItemPlaceholder}"
                    alt="placeholder"
                    width="50"
                    height="50"
                    loading="lazy"
                  >
                </div>`;

            return `
              <li id="predictive-search-option-collection-${i + 1}"
                  class="predictive-search__list-item"
                  role="option"
                  aria-selected="false"
                  data-collection-path="${path}"
                  ${needsFirst ? 'data-need-first="1"' : ''}
                  ${perItemPlaceholder ? `data-placeholder-image-url="${perItemPlaceholder.replace(/"/g, '&quot;')}"` : ''}>
                <a href="${href}"
                   class="predictive-search__item predictive-search__item--link-with-thumbnail link link--text"
                   tabindex="-1">
                  ${imageBlock}
                  <div class="predictive-search__item-content predictive-search__item-content--centered">
                    <p class="predictive-search__item-heading m-0">${title}</p>
                  </div>
                </a>
              </li>
            `;
          }).join('');

          list.innerHTML = itemsHtml;

          // Post-render enhancement: ONLY items we explicitly flagged
          const needSwap = Array.from(list.querySelectorAll('li[data-need-first="1"]'));
          if (needSwap.length) {
            const controller = new AbortController();
            const concurrent = 3;
            let idx = 0;

            const worker = async () => {
              while (idx < needSwap.length) {
                const li = needSwap[idx++];
                const path = li.getAttribute('data-collection-path');
                const img = li.querySelector('img.predictive-search__image');
                if (!path || !img) continue;

                const fetched = await fetchFirstProductImage(path, controller.signal);
                if (fetched) {
                  img.removeAttribute('srcset');
                  img.removeAttribute('data-src');
                  img.removeAttribute('data-srcset');
                  img.removeAttribute('data-sizes');
                  img.loading = 'eager';
                  img.decoding = 'sync';
                  img.src = ensureWidth(fetched);
                  if ((img.getAttribute('alt') || '').toLowerCase() === 'placeholder') {
                    const t = li.querySelector('.predictive-search__item-heading')?.textContent?.trim() || '';
                    if (t) img.setAttribute('alt', t);
                  }
                  li.removeAttribute('data-need-first');
                }
              }
            };

            await Promise.all(Array.from({ length: Math.min(concurrent, needSwap.length) }, worker));
          }
        }
      }

      // Final merged markup (now with explicit post-render swaps)
      const mergedMarkup = sectionRoot.innerHTML;
      this.cacheAndRender(queryKey, mergedMarkup);
    })
    .catch((error) => {
      if (error?.code === 20) {
        // Aborted
        return;
      }
      this.close();
      throw error;
    });
}

  cacheAndRender(queryKey, resultsMarkup) {
    // Save bandwidth keeping the cache in all instances synced
    this.allPredictiveSearchInstances.forEach((instance) => {
      instance.cachedResults[queryKey] = resultsMarkup;
    });
    this.renderSearchResults(resultsMarkup);
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);
    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight - document.querySelector('.section-header')?.getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');
    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }
}

customElements.define('predictive-search', PredictiveSearch);